'use client';

import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

export default function GettingStartedPage() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-950 dark:via-blue-950/30 dark:to-indigo-950/20">
      {/* Animated background orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000" />

      <div className="relative container mx-auto px-4 py-16 pt-32 max-w-5xl">
        {/* Back button */}
        <Link
          href="/en/resources/documentation"
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl border border-white/20 dark:border-slate-700/50 text-slate-700 dark:text-slate-300  transition-all mb-12"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">Back to Documentation</span>
        </Link>
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent leading-tight mb-6">
            Getting Started with Next PAL Nexus
          </h1>
          <p className="text-2xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Get your PostgreSQL backups running in under 10 minutes with this quick start guide.
          </p>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="absolute -inset-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl rounded-2xl -z-10" />
          <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">

        <h2>Prerequisites</h2>
        <ul>
          <li>PostgreSQL 12 or higher</li>
          <li>Linux server with SSH access</li>
          <li>Root or sudo privileges</li>
          <li>At least 10GB free disk space</li>
        </ul>

        <h2>Step 1: Create Your Account</h2>
        <p>Sign up for a free Next PAL Nexus account at <code>/signup</code>. No credit card required for the free tier.</p>

        <h2>Step 2: Add Your First Server</h2>
        <ol>
          <li>Navigate to <strong>Servers</strong> in the dashboard</li>
          <li>Click <strong>Add Server</strong></li>
          <li>Enter your server details:
            <ul>
              <li>Server name (e.g., "Production DB")</li>
              <li>Hostname or IP address</li>
              <li>SSH port (default: 22)</li>
              <li>SSH username</li>
            </ul>
          </li>
          <li>Upload your SSH key or generate a new one</li>
          <li>Click <strong>Test Connection</strong> to verify</li>
        </ol>

        <h2>Step 3: Install pgBackRest</h2>
        <p>Next PAL Nexus can automatically install pgBackRest on your server:</p>
        <ol>
          <li>Go to <strong>Server Settings</strong></li>
          <li>Click <strong>Install Software</strong></li>
          <li>Select <strong>pgBackRest</strong></li>
          <li>Click <strong>Install</strong> and wait for completion</li>
        </ol>

        <h2>Step 4: Configure Your First Backup</h2>
        <ol>
          <li>Navigate to <strong>Backups</strong> → <strong>New Backup</strong></li>
          <li>Select your server</li>
          <li>Choose backup type:
            <ul>
              <li><strong>Full</strong> - Complete database backup</li>
              <li><strong>Differential</strong> - Changes since last full</li>
              <li><strong>Incremental</strong> - Changes since last backup</li>
            </ul>
          </li>
          <li>Configure retention policy (e.g., keep 7 daily, 4 weekly)</li>
          <li>Click <strong>Create Backup</strong></li>
        </ol>

        <h2>Step 5: Schedule Automated Backups</h2>
        <ol>
          <li>Go to <strong>Schedules</strong> → <strong>New Schedule</strong></li>
          <li>Set up your backup schedule:
            <ul>
              <li>Full backup: Weekly (Sunday 2 AM)</li>
              <li>Differential: Daily (2 AM)</li>
              <li>Incremental: Every 4 hours</li>
            </ul>
          </li>
          <li>Enable notifications for failures</li>
          <li>Save and activate</li>
        </ol>

        <h2>Step 6: Verify Your Backups</h2>
        <p>Always test your backups to ensure they work:</p>
        <ol>
          <li>Navigate to <strong>Backups</strong></li>
          <li>Select a recent backup</li>
          <li>Click <strong>Verify</strong></li>
          <li>Review the verification report</li>
        </ol>

        <h2>Next Steps</h2>
        <ul>
          <li>Set up <strong>Point-in-Time Recovery</strong> for granular restore options</li>
          <li>Configure <strong>Alerts</strong> for backup failures</li>
          <li>Add more servers to your infrastructure</li>
          <li>Explore <strong>Analytics</strong> to monitor backup health</li>
        </ul>

        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-bold mb-2">Need Help?</h3>
          <p className="mb-0">
            Join our community on Discord or contact support at <a href="mailto:friends@next-pal.com">friends@next-pal.com</a>
          </p>
        </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
